import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "mm_table_session";
const TableSessionCtx = createContext(null);

// Sentinel value used for the front-of-house "pre-order" QR (customer scans
// on the way in, picks their table when they sit down).
const DOOR = "door";

export function TableSessionProvider({ children }) {
  const [table, setTable] = useState(null);

  // On first mount: check URL params (from QR scan), then fall back to localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("table");
    if (t && (/^\d{1,3}$/.test(t) || t.toLowerCase() === DOOR)) {
      const normalised = t.toLowerCase() === DOOR ? DOOR : String(parseInt(t, 10));
      const payload = { table: normalised, since: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      setTable(normalised);
      return;
    }
    // Restore existing session if still fresh (12 hours)
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const age = Date.now() - (parsed.since || 0);
      if (age < 12 * 60 * 60 * 1000 && parsed.table) {
        setTable(String(parsed.table));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      console.warn("[table-session] localStorage read failed", err);
    }
  }, []);

  const endSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setTable(null);
  }, []);

  const startSession = useCallback((num) => {
    const val = String(num).toLowerCase() === DOOR ? DOOR : String(num);
    const payload = { table: val, since: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setTable(val);
  }, []);

  const isPreOrder = table === DOOR;

  const value = useMemo(
    () => ({ table, isPreOrder, endSession, startSession }),
    [table, isPreOrder, endSession, startSession]
  );

  return (
    <TableSessionCtx.Provider value={value}>
      {children}
    </TableSessionCtx.Provider>
  );
}

export function useTableSession() {
  return useContext(TableSessionCtx) || { table: null, isPreOrder: false, endSession: () => {}, startSession: () => {} };
}
